import { useState, useEffect, useCallback } from 'react';
import { FiSave, FiSettings, FiGlobe, FiDollarSign, FiMail, FiLink, FiFileText, FiAlertTriangle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getAdminSettings, updateAdminSettings } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminSettings.css';

const TABS = [
  { key: 'general',     label: 'General',     icon: <FiSettings size={15} /> },
  { key: 'commerce',    label: 'Commerce',    icon: <FiDollarSign size={15} /> },
  { key: 'social',      label: 'Social Links', icon: <FiLink size={15} /> },
  { key: 'smtp',        label: 'Email (SMTP)', icon: <FiMail size={15} /> },
  { key: 'policies',    label: 'Policies',    icon: <FiFileText size={15} /> },
  { key: 'maintenance', label: 'Maintenance', icon: <FiAlertTriangle size={15} /> },
];

const DEFAULTS = {
  platformName: 'Fivora',
  platformEmail: '',
  platformPhone: '',
  logoUrl: '',
  faviconUrl: '',
  currency: 'INR',
  currencySymbol: 'Rs. ',
  commissionRate: 10,
  shippingFreeThreshold: 500,
  allowVendorRegistration: true,
  requireVendorApproval: true,
  maintenanceMode: false,
  maintenanceMessage: '',
  smtpConfig: { host: '', port: 587, user: '', password: '', from: '' },
  socialLinks: { facebook: '', instagram: '', twitter: '', youtube: '', linkedin: '' },
  policies: { returnDays: 30, warrantyInfo: '', privacyPolicy: '', termsOfService: '' },
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await getAdminSettings();
      const s = data.settings || {};
      setSettings({
        ...DEFAULTS,
        ...s,
        smtpConfig: { ...DEFAULTS.smtpConfig, ...(s.smtpConfig || {}) },
        socialLinks: { ...DEFAULTS.socialLinks, ...(s.socialLinks || {}) },
        policies: { ...DEFAULTS.policies, ...(s.policies || {}) },
      });
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings((s) => ({ ...s, [field]: val }));
  };

  const setNested = (group, field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings((s) => ({ ...s, [group]: { ...s[group], [field]: val } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      payload.commissionRate = Number(payload.commissionRate);
      payload.shippingFreeThreshold = Number(payload.shippingFreeThreshold);
      payload.smtpConfig = { ...payload.smtpConfig, port: Number(payload.smtpConfig.port) };
      payload.policies = { ...payload.policies, returnDays: Number(payload.policies.returnDays) };
      await updateAdminSettings(payload);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <AdminLayout title="Settings">
      <div className="loading-center"><div className="spinner" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout
      title="Platform Settings"
      action={
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner spinner-sm" /> Saving…</> : <><FiSave size={14} /> Save Changes</>}
        </button>
      }
    >
      <div className="as-page">
        {/* Tab bar */}
        <div className="as-tabs card">
          <div className="as-tabs-inner">
            {TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                className={`as-tab${activeTab === key ? ' active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="as-content card">
          <div className="card-body">

            {/* ── General ───────────────────────────────────────────────────── */}
            {activeTab === 'general' && (
              <div className="as-section">
                <h3 className="as-section-title">General Information</h3>
                <div className="as-grid-2">
                  <div className="form-group">
                    <label className="form-label">Platform Name</label>
                    <input className="form-input" value={settings.platformName} onChange={set('platformName')} placeholder="Fivora" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Support Email</label>
                    <input className="form-input" type="email" value={settings.platformEmail} onChange={set('platformEmail')} placeholder="support@fivora.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Support Phone</label>
                    <input className="form-input" value={settings.platformPhone} onChange={set('platformPhone')} placeholder="+91 98765 43210" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Logo URL</label>
                    <input className="form-input" value={settings.logoUrl} onChange={set('logoUrl')} placeholder="https://..." />
                    <span className="form-hint">Direct link to logo image (SVG/PNG, recommended 200×50)</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Favicon URL</label>
                    <input className="form-input" value={settings.faviconUrl} onChange={set('faviconUrl')} placeholder="https://..." />
                    <span className="form-hint">Direct link to favicon (ICO/PNG, 32×32)</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Commerce ──────────────────────────────────────────────────── */}
            {activeTab === 'commerce' && (
              <div className="as-section">
                <h3 className="as-section-title">Commerce Settings</h3>
                <div className="as-grid-2">
                  <div className="form-group">
                    <label className="form-label">Currency Code</label>
                    <input className="form-input" value={settings.currency} onChange={set('currency')} placeholder="INR" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Currency Symbol</label>
                    <input className="form-input" value={settings.currencySymbol} onChange={set('currencySymbol')} placeholder="Rs. " />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Platform Commission (%)</label>
                    <input className="form-input" type="number" min="0" max="100" step="0.5" value={settings.commissionRate} onChange={set('commissionRate')} />
                    <span className="form-hint">Percentage taken from each sale</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Free Shipping Threshold (Rs. )</label>
                    <input className="form-input" type="number" min="0" value={settings.shippingFreeThreshold} onChange={set('shippingFreeThreshold')} />
                    <span className="form-hint">Orders above this amount get free shipping</span>
                  </div>
                </div>

                <h3 className="as-section-title" style={{ marginTop: '2rem' }}>Vendor Registration</h3>
                <div className="as-toggle-list">
                  <label className="as-toggle">
                    <input type="checkbox" checked={settings.allowVendorRegistration} onChange={set('allowVendorRegistration')} />
                    <span className="as-toggle-slider" />
                    <div>
                      <span className="as-toggle-label">Allow vendor registration</span>
                      <span className="as-toggle-hint">When disabled, new vendors cannot apply</span>
                    </div>
                  </label>
                  <label className="as-toggle">
                    <input type="checkbox" checked={settings.requireVendorApproval} onChange={set('requireVendorApproval')} />
                    <span className="as-toggle-slider" />
                    <div>
                      <span className="as-toggle-label">Require admin approval for new vendors</span>
                      <span className="as-toggle-hint">When enabled, vendors must be manually approved</span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* ── Social Links ──────────────────────────────────────────────── */}
            {activeTab === 'social' && (
              <div className="as-section">
                <h3 className="as-section-title">Social Media Links</h3>
                <div className="as-grid-2">
                  {['facebook', 'instagram', 'twitter', 'youtube', 'linkedin'].map((platform) => (
                    <div key={platform} className="form-group">
                      <label className="form-label">{platform.charAt(0).toUpperCase() + platform.slice(1)}</label>
                      <input
                        className="form-input"
                        value={settings.socialLinks[platform] || ''}
                        onChange={setNested('socialLinks', platform)}
                        placeholder={`https://${platform}.com/yourpage`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SMTP ──────────────────────────────────────────────────────── */}
            {activeTab === 'smtp' && (
              <div className="as-section">
                <h3 className="as-section-title">SMTP Email Configuration</h3>
                <p className="as-section-desc">Configure outgoing email for order confirmations, password resets, and notifications.</p>
                <div className="as-grid-2">
                  <div className="form-group">
                    <label className="form-label">SMTP Host</label>
                    <input className="form-input" value={settings.smtpConfig.host} onChange={setNested('smtpConfig', 'host')} placeholder="smtp.gmail.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Port</label>
                    <input className="form-input" type="number" value={settings.smtpConfig.port} onChange={setNested('smtpConfig', 'port')} placeholder="587" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SMTP Username</label>
                    <input className="form-input" value={settings.smtpConfig.user} onChange={setNested('smtpConfig', 'user')} placeholder="your@email.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SMTP Password</label>
                    <input className="form-input" type="password" value={settings.smtpConfig.password} onChange={setNested('smtpConfig', 'password')} placeholder="App password or SMTP secret" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">From Address</label>
                    <input className="form-input" value={settings.smtpConfig.from} onChange={setNested('smtpConfig', 'from')} placeholder='"Fivora" <no-reply@fivora.com>' />
                  </div>
                </div>
              </div>
            )}

            {/* ── Policies ──────────────────────────────────────────────────── */}
            {activeTab === 'policies' && (
              <div className="as-section">
                <h3 className="as-section-title">Return Policy</h3>
                <div className="form-group" style={{ maxWidth: 300 }}>
                  <label className="form-label">Return Window (days)</label>
                  <input className="form-input" type="number" min="0" value={settings.policies.returnDays} onChange={setNested('policies', 'returnDays')} />
                  <span className="form-hint">Buyers can request returns within this many days of delivery</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Warranty Information</label>
                  <textarea className="form-textarea" rows={3} value={settings.policies.warrantyInfo} onChange={setNested('policies', 'warrantyInfo')} placeholder="Describe your warranty policy..." />
                </div>

                <h3 className="as-section-title" style={{ marginTop: '2rem' }}>Legal Pages</h3>
                <div className="form-group">
                  <label className="form-label">Privacy Policy</label>
                  <textarea className="form-textarea" rows={6} value={settings.policies.privacyPolicy} onChange={setNested('policies', 'privacyPolicy')} placeholder="Paste your privacy policy text here..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Terms of Service</label>
                  <textarea className="form-textarea" rows={6} value={settings.policies.termsOfService} onChange={setNested('policies', 'termsOfService')} placeholder="Paste your terms of service here..." />
                </div>
              </div>
            )}

            {/* ── Maintenance ───────────────────────────────────────────────── */}
            {activeTab === 'maintenance' && (
              <div className="as-section">
                <h3 className="as-section-title">Maintenance Mode</h3>
                <p className="as-section-desc">When enabled, the storefront displays a maintenance message to visitors. Admin panel remains accessible.</p>

                <div className="as-maintenance-card">
                  <label className="as-toggle as-toggle-lg">
                    <input type="checkbox" checked={settings.maintenanceMode} onChange={set('maintenanceMode')} />
                    <span className="as-toggle-slider" />
                    <div>
                      <span className="as-toggle-label">Enable Maintenance Mode</span>
                      <span className="as-toggle-hint">Site will show a maintenance message to all visitors</span>
                    </div>
                  </label>
                </div>

                {settings.maintenanceMode && (
                  <div className="form-group" style={{ marginTop: '1.5rem' }}>
                    <label className="form-label">Maintenance Message</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={settings.maintenanceMessage}
                      onChange={set('maintenanceMessage')}
                      placeholder="We're performing scheduled maintenance. We'll be back shortly!"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Save footer */}
        <div className="as-footer">
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner spinner-sm" /> Saving…</> : <><FiSave size={16} /> Save All Settings</>}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
